import { useAtomValue, useSetAtom } from "jotai";
import { ExternalLink, Cylinder, Map as MapIcon, Settings } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "wouter";
import type { AUTSector } from "@/api/queries/account";
import { Modal, Tabs } from "@/components";
import type { ControllerConfigValue } from "@/components/ControllerConfigurationPanel";
import {
  appShell,
  createProjectAtom,
  createSectorAtom,
  projectsAtom,
  refetchDataAtom,
  selectedPropertyAtom,
  updateProjectAtom,
  updateSectorAtom,
  waterPumpsAtom,
} from "@/store";
import {
  createSectorCropAtom,
  createVirtualDeviceConfigurationAtom,
  deleteSectorAtom,
  updateSectorCropAtom,
  updateVirtualDeviceConfigurationAtom,
} from "@/store/crud";
import {
  deviceToLicMapAtom,
  projectBackwashVirtualDeviceConfigMapAtom,
  projectByIdAtom,
  propertyDevicesAtom,
  sectorsByProjectIdAtom,
  sectorVirtualDeviceConfigMapAtom,
  waterPumpVirtualDeviceConfigMapAtom,
} from "@/store/data";
import { isLICDevice } from "@/utils/mesh-device-utils";
import AssignTankDialog from "./components/AssignTankDialog";
import ProjectConfigPanel from "./components/ProjectConfigPanel";
import ProjectTanksPanel from "./components/ProjectTanksPanel";
import SectorDetailPanel from "./components/SectorDetailPanel";
import SectorsPanel from "./components/SectorsPanel";

// Form interfaces for the component
interface ProjectConfigForm {
  id: string;
  name: string;
  description: string;
  start_date: string;
  end_date: string;
  irrigation_water_pump: string | null;
  fertigation_water_pump: string | null;
  localized_irrigation_controller: string | null;
  pipe_wash_time_seconds: string; // in seconds (stored as string to allow empty values)
  backwash_duration_seconds: number | null; // in seconds
  backwash_period_seconds: number | null; // in seconds
  // backwash_pump_type: "IRRIGATION" | "FERTIGATION" | "";
}

export interface SectorForm {
  name: string;
  description: string;
  area: number;
  power: number;
  coordinates: { type: "Point"; coordinates: [number, number] } | null;
  altitude: number | null;
  cropTypeId: string | null;
  cropStartDate: string | null;
  cropPlantPopulation: number | null;
  cropPlantingDate: string | null;
}

/** Structural equality for two optional GeoJSON Points (compares lon/lat only). */
const pointsEqual = (
  a: { coordinates?: number[] } | null | undefined,
  b: { coordinates?: number[] } | null | undefined
): boolean => {
  if (!a && !b) return true;
  if (!a || !b) return false;
  const ax = a.coordinates;
  const bx = b.coordinates;
  return !!ax && !!bx && ax[0] === bx[0] && ax[1] === bx[1];
};

/**
 * Computes only the sector fields that actually changed between the stored
 * sector and the form values. Returns the partial PATCH payload, or an empty
 * object when nothing changed — so the save can skip the write entirely and
 * avoid spurious timestamp/CDC triggers on no-op saves.
 */
const computeSectorChanges = (
  existing: AUTSector,
  form: SectorForm
): {
  name?: string;
  description?: string | null;
  area?: number;
  power?: number;
  coordinates?: { type: "Point"; coordinates: [number, number] } | null;
  altitude?: number | null;
} => {
  const changes: {
    name?: string;
    description?: string | null;
    area?: number;
    power?: number;
    coordinates?: { type: "Point"; coordinates: [number, number] } | null;
    altitude?: number | null;
  } = {};

  if (form.name !== existing.name) changes.name = form.name;

  const description = form.description || null;
  if (description !== (existing.description ?? null)) changes.description = description;

  if (form.area !== (existing.area ?? 0)) changes.area = form.area;

  if (form.power !== existing.power) changes.power = form.power;

  if (form.altitude !== existing.altitude) changes.altitude = form.altitude;

  if (!pointsEqual(form.coordinates, existing.coordinates)) changes.coordinates = form.coordinates;

  return changes;
};

// Define tabs configuration with full type safety
const PROJECT_CONFIG_TABS = [
  { key: "config", label: "Configuração", icon: Settings },
  { key: "sectors", label: "Setores", icon: MapIcon },
  { key: "tanks", label: "Tanques", icon: Cylinder },
] as const;

export type ProjectConfigPageProps = {
  projectId: string;
  activeTab: string;
};

function ProjectConfigPage({ projectId, activeTab: initialActiveTab }: ProjectConfigPageProps) {
  if (initialActiveTab && !PROJECT_CONFIG_TABS.some((tab) => tab.key === initialActiveTab)) {
    initialActiveTab = "config"; // Default to config if invalid
  }
  const [activeTab, setActiveTab] = useState(initialActiveTab || "config");

  const setBackButton = useSetAtom(appShell.backButtonAtom);
  useEffect(() => {
    const backPath = projectId === "new" ? "/app/projects" : `/app/projects/${projectId}`;
    setBackButton(backPath);
    return () => {
      setBackButton(false);
    };
  }, [setBackButton, projectId]);

  const [, setLocation] = useLocation();

  const projectById = useAtomValue(projectByIdAtom);
  const sectorsByProjectId = useAtomValue(sectorsByProjectIdAtom);

  const project = useMemo(() => {
    return projectById(projectId);
  }, [projectById, projectId]);
  const property = useAtomValue(selectedPropertyAtom);
  const sectors = useMemo(() => {
    return sectorsByProjectId(projectId);
  }, [sectorsByProjectId, projectId]);
  const propertyDevices = useAtomValue(propertyDevicesAtom);
  const waterPumps = useAtomValue(waterPumpsAtom);
  const deviceToLicMap = useAtomValue(deviceToLicMapAtom);
  const waterPumpVirtualDeviceConfigMap = useAtomValue(waterPumpVirtualDeviceConfigMapAtom);
  const sectorVirtualDeviceConfigMap = useAtomValue(sectorVirtualDeviceConfigMapAtom);
  const projectBackwashVirtualDeviceConfig = useAtomValue(projectBackwashVirtualDeviceConfigMapAtom);

  // CRUD operations
  const createProject = useSetAtom(createProjectAtom);
  const updateProject = useSetAtom(updateProjectAtom);
  const createSector = useSetAtom(createSectorAtom);
  const updateSector = useSetAtom(updateSectorAtom);
  const deleteSector = useSetAtom(deleteSectorAtom);
  const createVirtualDeviceConfiguration = useSetAtom(createVirtualDeviceConfigurationAtom);
  const updateVirtualDeviceConfiguration = useSetAtom(updateVirtualDeviceConfigurationAtom);
  const createSectorCrop = useSetAtom(createSectorCropAtom);
  const updateSectorCrop = useSetAtom(updateSectorCropAtom);
  const refetchData = useSetAtom(refetchDataAtom);

  const [saving, setSaving] = useState(false);
  const [showSectorDetail, setShowSectorDetail] = useState(false);
  const [editingSector, setEditingSector] = useState<AUTSector | undefined>(undefined);
  const [showAssignTank, setShowAssignTank] = useState(false);

  const [formData, setFormData] = useState<ProjectConfigForm>({
    id: "",
    name: "",
    description: "",
    start_date: "",
    end_date: "",
    irrigation_water_pump: null,
    fertigation_water_pump: null,
    localized_irrigation_controller: null,
    pipe_wash_time_seconds: "",
    backwash_duration_seconds: null,
    backwash_period_seconds: null,
  });

  // Backwash controller configuration state
  const [backwashControllerConfig, setBackwashControllerConfig] = useState<ControllerConfigValue>({
    controllerId: null,
    turnOnOutputIndex: null,
    turnOffOutputIndex: null,
    monitoringInputIndex: null,
    outputMode: "continuous",
    inputMode: "digital",
  });

  const isNewProject = projectId === "new";

  // Check if the project's irrigation pump is in analog mode
  const hasAnalogPumpMode = useMemo(() => {
    if (!project || (!project.irrigation_water_pump && !formData.irrigation_water_pump)) return false;

    // Use current project data or form data for irrigation pump
    const irrigationPumpId = project?.irrigation_water_pump || formData.irrigation_water_pump;
    if (!irrigationPumpId) return false;

    const virtualConfig = waterPumpVirtualDeviceConfigMap.get(irrigationPumpId);
    return virtualConfig?.mode === "analog";
  }, [project, formData.irrigation_water_pump, waterPumpVirtualDeviceConfigMap]);

  // Create a map from a water pump ID to its LIC's device ID
  const pumpToLicMap = useMemo(() => {
    const map = new Map<string, string | null>();
    waterPumps.forEach((pump) => {
      const virtualConfig = waterPumpVirtualDeviceConfigMap.get(pump.id);
      const controller = virtualConfig?.device?.id;
      let controllerId: string | null = null;
      if (typeof controller === "object" && controller !== null) {
        controllerId = (controller as any).id;
      } else if (typeof controller === "string") {
        controllerId = controller;
      }

      if (pump.id && controllerId) {
        map.set(pump.id, deviceToLicMap.get(controllerId) ?? null);
      }
    });
    return map;
  }, [waterPumps, deviceToLicMap, waterPumpVirtualDeviceConfigMap]);

  useEffect(() => {
    if (isNewProject) {
      // For new projects, keep form data empty
      setFormData({
        id: "",
        name: "",
        description: "",
        start_date: "",
        end_date: "",
        irrigation_water_pump: null,
        fertigation_water_pump: null,
        localized_irrigation_controller: null,
        pipe_wash_time_seconds: "",
        backwash_duration_seconds: null,
        backwash_period_seconds: null,
      });
      // Reset backwash controller config for new projects
      setBackwashControllerConfig({
        controllerId: null,
        turnOnOutputIndex: null,
        turnOffOutputIndex: null,
        monitoringInputIndex: null,
        outputMode: "continuous",
        inputMode: "digital",
      });
    } else if (project) {
      // For existing projects, populate form with project data
      setFormData({
        id: project.id,
        name: project.name,
        description: project.description || "",
        start_date: project.start_date || "",
        end_date: project.end_date || "",
        irrigation_water_pump: project.irrigation_water_pump || null,
        fertigation_water_pump: project.fertigation_water_pump || null,
        localized_irrigation_controller: project.localized_irrigation_controller,
        pipe_wash_time_seconds: project.pipe_wash_time_seconds?.toString() || "",
        backwash_duration_seconds: project.backwash_duration_seconds,
        backwash_period_seconds: project.backwash_period_seconds,
      });

      // Initialize backwash controller config from existing configuration
      const currentBackwashConfig = projectBackwashVirtualDeviceConfig.get(project.id);
      if (currentBackwashConfig) {
        setBackwashControllerConfig({
          controllerId: currentBackwashConfig.device.id,
          turnOnOutputIndex: currentBackwashConfig.turn_on_physical_output_index,
          turnOffOutputIndex: currentBackwashConfig.turn_off_physical_output_index,
          monitoringInputIndex: currentBackwashConfig.monitoring_physical_input_index,
          outputMode: currentBackwashConfig.mode,
          inputMode: currentBackwashConfig.monitoring_input_mode || "digital",
        });
      } else {
        setBackwashControllerConfig({
          controllerId: null,
          turnOnOutputIndex: null,
          turnOffOutputIndex: null,
          monitoringInputIndex: null,
          outputMode: "continuous",
          inputMode: "digital",
        });
      }
    }
  }, [isNewProject, project, projectBackwashVirtualDeviceConfig]);

  const handleInputChange = (field: keyof ProjectConfigForm, value: string | null) => {
    setFormData((prev) => {
      const newState = { ...prev, [field]: value };

      // When LIC changes, clear the pump selections if they don't match
      if (field === "localized_irrigation_controller") {
        const selectedLicId = value;
        const irrigationPumpId = newState.irrigation_water_pump;
        const fertigationPumpId = newState.fertigation_water_pump;

        if (irrigationPumpId && pumpToLicMap.get(irrigationPumpId) !== selectedLicId) {
          newState.irrigation_water_pump = null;
        }
        if (fertigationPumpId && pumpToLicMap.get(fertigationPumpId) !== selectedLicId) {
          newState.fertigation_water_pump = null;
        }
      }

      return newState;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (isNewProject) {
        // Create new project
        const projectData = {
          property: property?.id, // Add the required property field
          name: formData.name,
          description: formData.description || null,
          start_date: formData.start_date || null,
          end_date: formData.end_date || null,
          irrigation_water_pump: formData.irrigation_water_pump || null,
          fertigation_water_pump: formData.fertigation_water_pump || null,
          localized_irrigation_controller: formData.localized_irrigation_controller || null,
          pipe_wash_time_seconds:
            formData.pipe_wash_time_seconds.trim() !== "" ? parseInt(formData.pipe_wash_time_seconds) : null,
          backwash_duration_seconds: formData.backwash_duration_seconds ? formData.backwash_duration_seconds : null,
          backwash_period_seconds: formData.backwash_period_seconds ? formData.backwash_period_seconds : null,
        };

        const result = await createProject(projectData);
        if (result.success && result.data?.id) {
          const newProjectId = result.data.id;

          // Create backwash controller configuration if set
          if (backwashControllerConfig.controllerId && backwashControllerConfig.turnOnOutputIndex != null) {
            // Find which pump is using this controller device
            const irrigationPumpConfig = formData.irrigation_water_pump
              ? waterPumpVirtualDeviceConfigMap.get(formData.irrigation_water_pump)
              : null;
            const fertigationPumpConfig = formData.fertigation_water_pump
              ? waterPumpVirtualDeviceConfigMap.get(formData.fertigation_water_pump)
              : null;

            // Determine which pump the backwash controller belongs to
            let backwashWaterPumpId: string | undefined;
            if (irrigationPumpConfig?.device?.id === backwashControllerConfig.controllerId) {
              backwashWaterPumpId = formData.irrigation_water_pump ?? undefined;
            } else if (fertigationPumpConfig?.device?.id === backwashControllerConfig.controllerId) {
              backwashWaterPumpId = formData.fertigation_water_pump ?? undefined;
            }

            await createVirtualDeviceConfiguration({
              device: backwashControllerConfig.controllerId,
              mode: backwashControllerConfig.outputMode,
              turn_on_physical_output_index: backwashControllerConfig.turnOnOutputIndex as 1 | 2 | 3 | 4,
              turn_off_physical_output_index: backwashControllerConfig.turnOffOutputIndex as 1 | 2 | 3 | 4 | null,
              monitoring_physical_input_index: backwashControllerConfig.monitoringInputIndex as 1 | 2 | 3 | 4 | null,
              monitoring_input_mode: backwashControllerConfig.monitoringInputIndex
                ? backwashControllerConfig.inputMode
                : null,
              equipment_type: "project_backwash",
              project_backwash: newProjectId,
              water_pump: backwashWaterPumpId,
              start_date: new Date().toISOString(),
            });
          }

          await refetchData();
          setLocation(`/app/projects/${newProjectId}/config`);
        }
      } else {
        // Update existing project
        const projectData = {
          name: formData.name,
          description: formData.description || null,
          start_date: formData.start_date || null,
          end_date: formData.end_date || null,
          irrigation_water_pump: formData.irrigation_water_pump || null,
          fertigation_water_pump: formData.fertigation_water_pump || null,
          localized_irrigation_controller: formData.localized_irrigation_controller || null,
          pipe_wash_time_seconds:
            formData.pipe_wash_time_seconds.trim() !== "" ? parseInt(formData.pipe_wash_time_seconds) : null,
          backwash_duration_seconds: formData.backwash_duration_seconds ? formData.backwash_duration_seconds : null,
          backwash_period_seconds: formData.backwash_period_seconds ? formData.backwash_period_seconds : null,
        };

        const result = await updateProject({
          id: formData.id,
          data: projectData,
        });
        if (result.success) {
          // Handle backwash controller configuration changes
          const currentBackwashConfig = projectBackwashVirtualDeviceConfig.get(projectId);
          const backwashChanged =
            currentBackwashConfig &&
            backwashControllerConfig.controllerId &&
            (currentBackwashConfig.device.id !== backwashControllerConfig.controllerId ||
              currentBackwashConfig.turn_on_physical_output_index !== backwashControllerConfig.turnOnOutputIndex);

          // End previous backwash configuration if it exists and either changed or was removed
          if (currentBackwashConfig && (backwashChanged || !backwashControllerConfig.controllerId)) {
            await updateVirtualDeviceConfiguration({
              id: currentBackwashConfig.id,
              data: {
                end_date: new Date().toISOString(),
              },
            });
          }

          // Create new backwash configuration if controller is selected and (new or changed)
          if (
            backwashControllerConfig.controllerId &&
            backwashControllerConfig.turnOnOutputIndex != null &&
            (!currentBackwashConfig || backwashChanged)
          ) {
            // Find which pump is using this controller device
            const irrigationPumpConfig = formData.irrigation_water_pump
              ? waterPumpVirtualDeviceConfigMap.get(formData.irrigation_water_pump)
              : null;
            const fertigationPumpConfig = formData.fertigation_water_pump
              ? waterPumpVirtualDeviceConfigMap.get(formData.fertigation_water_pump)
              : null;

            // Determine which pump the backwash controller belongs to
            let backwashWaterPumpId: string | undefined;
            if (irrigationPumpConfig?.device?.id === backwashControllerConfig.controllerId) {
              backwashWaterPumpId = formData.irrigation_water_pump ?? undefined;
            } else if (fertigationPumpConfig?.device?.id === backwashControllerConfig.controllerId) {
              backwashWaterPumpId = formData.fertigation_water_pump ?? undefined;
            }

            await createVirtualDeviceConfiguration({
              device: backwashControllerConfig.controllerId,
              mode: backwashControllerConfig.outputMode,
              turn_on_physical_output_index: backwashControllerConfig.turnOnOutputIndex as 1 | 2 | 3 | 4,
              turn_off_physical_output_index: backwashControllerConfig.turnOffOutputIndex as 1 | 2 | 3 | 4 | null,
              monitoring_physical_input_index: backwashControllerConfig.monitoringInputIndex as 1 | 2 | 3 | 4 | null,
              monitoring_input_mode: backwashControllerConfig.monitoringInputIndex
                ? backwashControllerConfig.inputMode
                : null,
              equipment_type: "project_backwash",
              project_backwash: projectId,
              water_pump: backwashWaterPumpId,
              start_date: new Date().toISOString(),
            });
          }

          // If the project's LIC is now null, enforce clearing sectors' valve controller fields
          if (!projectData.localized_irrigation_controller) {
            const currentSectors = sectorsByProjectId(projectId) || [];
            for (const s of currentSectors) {
              // Only update if any of these fields are not null to avoid unnecessary writes
              const sectorConfig = sectorVirtualDeviceConfigMap.get(s.id);
              if (sectorConfig) {
                await updateVirtualDeviceConfiguration({
                  id: sectorConfig.id,
                  data: {
                    end_date: new Date().toISOString(),
                  },
                });
              }
            }
          }

          await refetchData();
          setLocation(`/app/projects/${projectId}`);
        }
      }
    } catch (error) {
      console.error("Error saving project config:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (isNewProject) {
      setLocation("/app/projects");
    } else {
      setLocation(`/app/projects/${projectId}`);
    }
  };

  const handleEditSector = (sectorId: string) => {
    const sector = sectors?.find((s) => s.id === sectorId);
    setEditingSector(sector);
    setShowSectorDetail(true);
  };

  const handleAddSector = () => {
    setEditingSector(undefined);
    setShowSectorDetail(true);
  };

  const handleSaveSector = async (
    sectorData: SectorForm,
    controllerConfig: import("@/components/ControllerConfigurationPanel").ControllerConfigValue | null
  ) => {
    // Defense-in-depth: population is required (total plant count) when a crop is selected.
    // Primary validation lives in SectorDetailPanel.handleSave.
    if (sectorData.cropTypeId && (sectorData.cropPlantPopulation == null || sectorData.cropPlantPopulation < 1)) {
      alert("Por favor, informe a população total de plantas para a cultura selecionada.");
      return;
    }

    const handleSectorCrop = async (sectorId: string): Promise<boolean> => {
      const existingCrops = editingSector?.sector_crops || [];
      const activeCrop = existingCrops.find((sc) => !sc.end_date) || null;

      if (sectorData.cropTypeId) {
        if (!activeCrop) {
          await createSectorCrop({
            sector: sectorId,
            crop_type: sectorData.cropTypeId,
            start_date: sectorData.cropStartDate || new Date().toISOString().split("T")[0],
            planting_date: sectorData.cropPlantingDate || undefined,
            plant_population: sectorData.cropPlantPopulation || undefined,
          });
          return true;
        } else if (activeCrop.crop_type.id !== sectorData.cropTypeId) {
          await updateSectorCrop({
            id: activeCrop.id,
            data: { end_date: new Date().toISOString() },
          });
          await createSectorCrop({
            sector: sectorId,
            crop_type: sectorData.cropTypeId,
            start_date: sectorData.cropStartDate || new Date().toISOString().split("T")[0],
            planting_date: sectorData.cropPlantingDate || undefined,
            plant_population: sectorData.cropPlantPopulation || undefined,
          });
          return true;
        } else {
          // Crop type unchanged — only PATCH when planting_date/plant_population
          // actually differ, to avoid a no-op write (and its timestamp/CDC triggers).
          const currentPlanting = activeCrop.planting_date ? activeCrop.planting_date.split("T")[0] : null;
          const formPlanting = sectorData.cropPlantingDate || null;
          const plantingChanged = formPlanting !== currentPlanting;
          const populationChanged = (sectorData.cropPlantPopulation ?? null) !== (activeCrop.plant_population ?? null);
          if (plantingChanged || populationChanged) {
            await updateSectorCrop({
              id: activeCrop.id,
              data: {
                planting_date: sectorData.cropPlantingDate || undefined,
                plant_population: sectorData.cropPlantPopulation || undefined,
              },
            });
            return true;
          }
        }
      } else if (activeCrop) {
        await updateSectorCrop({
          id: activeCrop.id,
          data: { end_date: new Date().toISOString() },
        });
        return true;
      }
      return false;
    };

    try {
      if (editingSector) {
        let dirty = false;
        try {
          // Only PATCH sector fields that actually changed — avoids spurious
          // writes (and their timestamp/CDC triggers) on no-op saves.
          const sectorChanges = computeSectorChanges(editingSector, sectorData);
          if (Object.keys(sectorChanges).length > 0) {
            const result = await updateSector({ id: editingSector.id, data: sectorChanges });
            if (!result.success) return;
            dirty = true;
          }

          const sectorConfig = sectorVirtualDeviceConfigMap.get(editingSector.id);

          const controllerChanged =
            sectorConfig &&
            controllerConfig &&
            (sectorConfig.turn_on_physical_output_index !== controllerConfig.turnOnOutputIndex ||
              sectorConfig.device.id !== controllerConfig.controllerId);

          if (sectorConfig && (controllerChanged || !controllerConfig)) {
            await updateVirtualDeviceConfiguration({
              id: sectorConfig.id,
              data: {
                end_date: new Date().toISOString(),
              },
            });
            dirty = true;
          }

          // Only create a new virtual_device_configuration if:
          // 1. The controller changed (existing one was already ended above)
          // 2. There was no existing configuration but a controller is now being set
          if (
            controllerConfig?.controllerId &&
            controllerConfig.turnOnOutputIndex != null &&
            (controllerChanged || !sectorConfig)
          ) {
            await createVirtualDeviceConfiguration({
              device: controllerConfig.controllerId,
              mode: controllerConfig.outputMode,
              turn_on_physical_output_index: controllerConfig.turnOnOutputIndex as 1 | 2 | 3 | 4,
              turn_off_physical_output_index: controllerConfig.turnOffOutputIndex as 1 | 2 | 3 | 4 | null,
              monitoring_physical_input_index: controllerConfig.monitoringInputIndex as 1 | 2 | 3 | 4 | null,
              monitoring_input_mode: controllerConfig.monitoringInputIndex ? controllerConfig.inputMode : null,
              equipment_type: "valve",
              sector: editingSector.id,
              start_date: new Date().toISOString(),
            });
            dirty = true;
          }

          if (await handleSectorCrop(editingSector.id)) dirty = true;
        } finally {
          // Only refetch when something was actually persisted — avoids a
          // full account-tree reload on a no-op save.
          if (dirty) {
            await refetchData();
          }
        }
      } else {
        await createSector({
          project: projectId!,
          name: sectorData.name,
          description: sectorData.description || null,
          area: sectorData.area,
          power: sectorData.power,
          coordinates: sectorData.coordinates,
          altitude: sectorData.altitude,
        })
          .then(async (result) => {
            if (!result.success || !result.data?.id) return;

            if (controllerConfig?.controllerId && controllerConfig.turnOnOutputIndex != null) {
              await createVirtualDeviceConfiguration({
                device: controllerConfig.controllerId,
                mode: controllerConfig.outputMode,
                turn_on_physical_output_index: controllerConfig.turnOnOutputIndex as 1 | 2 | 3 | 4,
                turn_off_physical_output_index: controllerConfig.turnOffOutputIndex as 1 | 2 | 3 | 4 | null,
                monitoring_physical_input_index: controllerConfig.monitoringInputIndex as 1 | 2 | 3 | 4 | null,
                monitoring_input_mode: controllerConfig.monitoringInputIndex ? controllerConfig.inputMode : null,
                equipment_type: "valve",
                sector: result.data.id,
                start_date: new Date().toISOString(),
              });
            }

            await handleSectorCrop(result.data.id);
          })
          .finally(async () => {
            await refetchData();
          });
      }
      setShowSectorDetail(false);
      setEditingSector(undefined);
    } catch (error) {
      console.error("Error saving sector:", error);
    }
  };

  const handleDeleteSector = async (sectorId: string) => {
    await deleteSector(sectorId);
    setShowSectorDetail(false);
  };

  const handleCancelSectorEdit = () => {
    setShowSectorDetail(false);
    setEditingSector(undefined);
  };

  const handleAssignTank = () => {
    setShowAssignTank(true);
  };

  const handleCloseAssignTank = () => {
    setShowAssignTank(false);
  };

  // Get all projects to check for assignments - MOVED BEFORE CONDITIONAL RETURN
  const allProjects = useAtomValue(projectsAtom);

  // Transform all devices for sectors (VC devices)
  const transformedAllDevices = useMemo(() => propertyDevices?.map(({ device }) => device) || [], [propertyDevices]);

  // Transform and filter LIC devices for project irrigation controllers
  const transformedLICDevices = useMemo(
    () =>
      propertyDevices?.filter(({ device }) => isLICDevice(device.model)) || // Only LIC devices for irrigation controllers
      [],
    [propertyDevices]
  );

  // Filter available LIC devices (not assigned to other projects)
  const availableLICDevices = useMemo(() => transformedLICDevices, [transformedLICDevices]);

  // Transform and filter water pumps
  const transformedWaterPumps = useMemo(
    () =>
      waterPumps?.map((pump) => ({
        id: pump.id,
        label: pump.label,
        identifier: pump.identifier,
        pumpType: pump.pump_type,
        pumpModel: pump.pump_model,
        controller: waterPumpVirtualDeviceConfigMap.get(pump.id) || null, // Get the associated controller device ID from the map
      })) || [],
    [waterPumps, waterPumpVirtualDeviceConfigMap]
  );

  // Filter available irrigation water pumps (IRRIGATION type, not assigned to other projects)
  const availableIrrigationPumps = useMemo(() => {
    return transformedWaterPumps.filter((pump) => {
      if (pump.pumpType !== "IRRIGATION") return false;

      // Check if this pump is assigned to any other project as irrigation pump
      const isAssignedToOtherProject = allProjects.some((proj: any) => {
        if (proj.id === projectId) return false; // Exclude current project
        const assignedPumpId =
          typeof proj.irrigation_water_pump === "object" ? proj.irrigation_water_pump?.id : proj.irrigation_water_pump;
        return assignedPumpId === pump.id;
      });
      if (isAssignedToOtherProject) return false;

      // Mesh network check
      const selectedLicId = formData.localized_irrigation_controller;
      if (selectedLicId) {
        const pumpLicId = pumpToLicMap.get(pump.id);
        return pumpLicId === selectedLicId;
      }

      return true; // Show if no LIC is selected yet
    });
  }, [transformedWaterPumps, allProjects, projectId, formData.localized_irrigation_controller, pumpToLicMap]);

  // Filter available fertigation water pumps (FERTIGATION type, not assigned to other projects)
  const availableFertigationPumps = useMemo(() => {
    return transformedWaterPumps.filter((pump) => {
      if (pump.pumpType !== "FERTIGATION") return false;

      // Check if this pump is assigned to any other project as fertigation pump
      const isAssignedToOtherProject = allProjects.some((proj: any) => {
        if (proj.id === projectId) return false; // Exclude current project
        const assignedPumpId =
          typeof proj.fertigation_water_pump === "object"
            ? proj.fertigation_water_pump?.id
            : proj.fertigation_water_pump;
        return assignedPumpId === pump.id;
      });
      if (isAssignedToOtherProject) return false;

      // Mesh network check
      const selectedLicId = formData.localized_irrigation_controller;
      if (selectedLicId) {
        const pumpLicId = pumpToLicMap.get(pump.id);
        return pumpLicId === selectedLicId;
      }

      return true; // Show if no LIC is selected yet
    });
  }, [transformedWaterPumps, allProjects, projectId, formData.localized_irrigation_controller, pumpToLicMap]);

  // // For fertigation pumps, we'll filter water pumps that are used for fertigation
  // const fertigationPumps = transformedWaterPumps.filter(
  //   (pump) =>
  //     pump.pumpType.toLowerCase().includes("fertigation") ||
  //     pump.pumpType.toLowerCase().includes("dosing")
  // );

  const transformedSectors = useMemo(
    () =>
      sectors?.map((sector) => {
        const sectorDeviceConfig = sectorVirtualDeviceConfigMap.get(sector.id);
        return {
          id: sector.id,
          projectId: sector.project, // Now always a string
          name: sector.name,
          description: sector.description || "",
          controllerId: sectorDeviceConfig?.device?.id || null, // Now always a string
          controllerOutput: sectorDeviceConfig?.turn_on_physical_output_index || null,
          area: sector.area || 0,
          power: sector.power,
        };
      }) || [],
    [sectors, sectorVirtualDeviceConfigMap]
  );

  // Transform form data for child components
  const transformedFormData = {
    id: formData.id,
    name: formData.name,
    description: formData.description,
    startDate: formData.start_date,
    endDate: formData.end_date,
    irrigationControllerId: formData.localized_irrigation_controller,
    waterPumpId: formData.irrigation_water_pump || "",
    fertigationPumpId: formData.fertigation_water_pump,
    pipeWashTimeMinutes:
      formData.pipe_wash_time_seconds.trim() !== "" ? Math.round(parseInt(formData.pipe_wash_time_seconds) / 60) : null,
    // Note: backwash fields are now read-only and come from global property
    backwashDurationMinutes: null,
    backwashPeriodMinutes: null,
    // backwashPumpType: formData.backwash_pump_type,
  };

  const transformedEditingSector = useMemo(
    () =>
      editingSector
        ? (() => {
            const activeCrop = editingSector.sector_crops?.find((sc) => !sc.end_date) || null;
            return {
              id: editingSector.id,
              projectId: editingSector.project,
              name: editingSector.name,
              description: editingSector.description || "",
              controllerId: sectorVirtualDeviceConfigMap.get(editingSector.id)?.device?.id || null,
              controllerOutput:
                sectorVirtualDeviceConfigMap.get(editingSector.id)?.turn_on_physical_output_index || null,
              area: editingSector.area || 0,
              power: editingSector.power,
              latitude: editingSector.coordinates?.coordinates[1] ?? null,
              longitude: editingSector.coordinates?.coordinates[0] ?? null,
              altitude: editingSector.altitude,
              currentCropTypeId: activeCrop?.crop_type.id || null,
              currentCropStartDate: activeCrop?.start_date?.split("T")[0] || null,
              currentCropPlantPopulation: activeCrop?.plant_population || null,
              currentCropPlantingDate: activeCrop?.planting_date?.split("T")[0] || null,
            };
          })()
        : undefined,
    [editingSector, sectorVirtualDeviceConfigMap]
  );
  // Show project not found for edit mode when no project is available
  if (!isNewProject && !project) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-neutral-900 mb-2">Projeto não encontrado</h2>
          <p className="text-neutral-600">
            O projeto solicitado não foi encontrado ou você não tem permissão para acessá-lo.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Fixed Header */}
      <div className="sticky top-0 bg-white z-10 border-b border-neutral-100 px-6 py-4 min-h-14">
        {/* Project Title */}
        <h4 className="text-lg font-medium text-neutral-900 mb-4 flex justify-between">
          <div>
            <span className="">{isNewProject ? "Novo Projeto" : "Projeto"}</span>{" "}
            {!isNewProject && <span className="font-semibold text-neutral-900">{formData.name}</span>}
          </div>
          {!isNewProject && (
            <button
              onClick={() => {
                setLocation(`/app/projects/${projectId}`);
              }}
              className="ml-4 px-3 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg border border-blue-200 transition-all duration-200 flex items-center gap-1.5"
            >
              Planejamento
              <ExternalLink className="w-4 h-4" />
            </button>
          )}
        </h4>
        {/* Top Tab Navigation */}
        <div className="mb-0">
          <Tabs
            tabs={isNewProject ? PROJECT_CONFIG_TABS.slice(0, 1) : PROJECT_CONFIG_TABS}
            onChange={setActiveTab}
            value={isNewProject ? "config" : activeTab}
            searchParamsKey="activeTab"
            className="bg-neutral-100 rounded-xl"
          />
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto ">
        {/* Tab Content */}
        {activeTab === "config" ? (
          <ProjectConfigPanel
            formData={transformedFormData}
            devices={availableLICDevices}
            waterPumps={availableIrrigationPumps}
            fertigationPumps={availableFertigationPumps}
            saving={saving}
            onInputChange={(field, value) => {
              // Map the old field names to new field names
              const fieldMap: Record<string, keyof ProjectConfigForm> = {
                startDate: "start_date",
                endDate: "end_date",
                irrigationControllerId: "localized_irrigation_controller",
                waterPumpId: "irrigation_water_pump",
                fertigationPumpId: "fertigation_water_pump",
                pipeWashTimeMinutes: "pipe_wash_time_seconds",
              };
              const mappedField = fieldMap[field] || (field as keyof ProjectConfigForm);
              // Convert value to seconds if needed (only for pipe wash time)
              // Handle empty strings properly - don't convert to 0
              if (field === "pipeWashTimeMinutes") {
                if (value?.trim() === "") {
                  value = ""; // Keep as empty string
                } else {
                  const numValue = Number(value);
                  value = isNaN(numValue) ? "" : (numValue * 60).toString();
                }
              }

              handleInputChange(mappedField, value);
            }}
            backwashControllerConfig={backwashControllerConfig}
            onBackwashControllerConfigChange={setBackwashControllerConfig}
            currentBackwashConfig={projectBackwashVirtualDeviceConfig.get(projectId) ?? undefined}
            onSave={handleSave}
            onCancel={handleCancel}
          />
        ) : activeTab === "tanks" ? (
          <ProjectTanksPanel projectId={projectId} onAssign={handleAssignTank} />
        ) : (
          <SectorsPanel
            sectors={transformedSectors}
            devices={transformedAllDevices}
            hasFrequencyInverter={hasAnalogPumpMode}
            onEditSector={handleEditSector}
            onAddSector={handleAddSector}
          />
        )}

        {/* Sector Detail Modal */}
        <Modal
          title={editingSector ? "Editar Setor" : "Adicionar Setor"}
          isOpen={showSectorDetail}
          onClose={handleCancelSectorEdit}
          showCloseButton={true}
        >
          <SectorDetailPanel
            sector={transformedEditingSector}
            projectId={projectId || ""}
            devices={transformedAllDevices}
            currentVirtualDeviceConfig={editingSector ? sectorVirtualDeviceConfigMap.get(editingSector.id) : undefined}
            hasAnalogPumpMode={hasAnalogPumpMode}
            onSave={handleSaveSector}
            onCancel={handleCancelSectorEdit}
            onDelete={(sectorId) => {
              handleDeleteSector(sectorId);
            }}
          />
        </Modal>

        {/* Assign Tank Dialog */}
        <AssignTankDialog projectId={projectId} isOpen={showAssignTank} onClose={handleCloseAssignTank} />
      </div>
    </div>
  );
}

export default ProjectConfigPage;
